import { styled } from "goober";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { zIndexes } from "../lib/zIndexes";

const CURSOR_OFFSET = 14;
const EDGE_PADDING = 8;

const Tip = styled("div")`
  position: fixed;
  z-index: ${zIndexes.tooltip};
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: max-content;
  max-width: 220px;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  font-size: 10px;
  line-height: 1.4;
  text-align: left;
  pointer-events: none;
`;

export const TooltipTitle = styled("div")`
  font-weight: 600;
  padding-bottom: 2px;
`;

export const TooltipRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const TooltipLabel = styled("span")`
  opacity: 0.7;
`;

export const TooltipValue = styled("span")`
  font-weight: 600;
`;

// A small tooltip that follows the cursor, flipping to the other side of it near
// a viewport edge so it never runs off-screen. `contentKey` triggers a re-measure
// when the content (and thus its size) changes.
export function CursorTooltip({
  x,
  y,
  contentKey,
  children,
}: {
  x: number;
  y: number;
  contentKey: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setSize({ width: el.offsetWidth, height: el.offsetHeight });
  }, [contentKey]);

  const { width, height } = size;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = x + CURSOR_OFFSET;
  if (width > 0 && left + width > vw - EDGE_PADDING) {
    left = Math.max(EDGE_PADDING, x - CURSOR_OFFSET - width);
  }

  let top = y + CURSOR_OFFSET;
  if (height > 0 && top + height > vh - EDGE_PADDING) {
    top = Math.max(EDGE_PADDING, y - CURSOR_OFFSET - height);
  }

  return (
    <Tip ref={ref} style={{ left, top }}>
      {children}
    </Tip>
  );
}
